import type { IRouteItem, MenuItem } from "@/app/ui/navigation/types";
import { salesMenu, buildSalesMenuItems } from "@/app/ui/navigation/salesMenu";
import {
  contactsMenu,
  buildContactsMenuItems,
} from "@/app/ui/navigation/contactsMenu";
import { stockMenu, buildStockMenuItems } from "@/app/ui/navigation/stockMenu";
import {
  distributionMenu,
  buildDistributionMenuItems,
} from "@/app/ui/navigation/distributionMenu";
import {
  productionMenu,
  buildProductionMenuItems,
} from "@/app/ui/navigation/productionMenu";
import {
  buildPricingMenuItems,
  pricingRoutes,
} from "@/app/ui/navigation/pricingMenu";
import {
  containerTrackingMenu,
  buildContainerTrackingMenuItems,
} from "@/app/ui/navigation/containerTrackingMenu";
import { buildDutyMenuItems } from "@/app/ui/navigation/dutyMenu";
import { buildReportingMenuItems } from "@/app/ui/navigation/reportingMenu";
import {
  integrationsMenu,
  buildIntegrationsMenuItems,
} from "@/app/ui/navigation/integrationsMenu";
import {
  adminRoutes,
  buildAdministrationMenuItems,
} from "@/app/ui/navigation/administrationMenu";
import {
  buildSupportMenuItems,
  supportRoutes,
} from "@/app/ui/navigation/supportMenu";
import {
  galleryRoutes,
  buildGalleryMenuItems,
} from "@/app/ui/navigation/galleryMenu";
import { buildMainMenuItems, mainRoutes } from "@/app/ui/navigation/mainMenu";
import {
  buildUllagesMenuItems,
  ullageRoutes,
} from "@/app/ui/navigation/ullagesMenu";
import {
  distributionAppMenu,
  buildDistributionAppItems,
} from "./distributionAppMenu";
import { accountRoutes } from "@/app/ui/navigation/accountManagement";
import { dutyMenuUk } from "@/app/ui/navigation/dutyMenuUk";
import { dutyMenuGerman } from "@/app/ui/navigation/dutyMenuGerman";
import { dutyMenuAustralian } from "@/app/ui/navigation/dutyMenuAustralian";
import {
  buildUnauthorisedItems,
  unauthorisedMenu,
} from "@/app/ui/navigation/unauthorisedMenu";
import { batchMenu, buildBatchMenuItems } from "@/app/ui/navigation/batchMenu";
import {
  multiTenantMenu,
  buildMultiTenantMenuItems,
} from "@/app/ui/navigation/multiTenantMenu";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import type { IResolvedSearchResult } from "@/app/services/extensions/searchServiceExtended";

export interface PathedMenuItem {
  path: readonly MenuItem[];
  menuItem: MenuItem;
}

export function buildMenuItems(): MenuItem[] {
  return [
    ...buildMainMenuItems(),
    ...buildSalesMenuItems(),
    ...buildContactsMenuItems(),
    ...buildStockMenuItems(),
    ...buildDistributionMenuItems(),
    ...buildProductionMenuItems(),
    ...buildBatchMenuItems(),
    ...buildUllagesMenuItems(),
    ...buildPricingMenuItems(),
    ...buildContainerTrackingMenuItems(),
    ...buildDutyMenuItems(),
    ...buildReportingMenuItems(),
    ...buildIntegrationsMenuItems(),
    ...buildAdministrationMenuItems(),
    ...buildMultiTenantMenuItems(),
    ...buildGalleryMenuItems(),
    ...buildSupportMenuItems(),
    ...buildDistributionAppItems(),
    ...buildUnauthorisedItems(),
  ];
}

export function buildRouteItems(): IRouteItem[] {
  const fromOldMenus: IRouteItem[] = [
    ...buildSalesMenuItems(),
    ...buildContactsMenuItems(),
    ...buildStockMenuItems(),
    ...buildDistributionMenuItems(),
    ...buildProductionMenuItems(),
    ...buildBatchMenuItems(),
    ...buildContainerTrackingMenuItems(),
    ...buildDutyMenuItems(),
    ...buildReportingMenuItems(),
    ...buildIntegrationsMenuItems(),
    ...buildMultiTenantMenuItems(),
    ...buildSupportMenuItems(),
    ...buildDistributionAppItems(),
    ...buildUnauthorisedItems(),
  ];

  // Because all the keys in Eg ullageRoutes are for IRouteItem
  // typescript knows that these all resolve to IRouteItem
  const fromRoutes = [
    mainRoutes,
    accountRoutes,
    ullageRoutes,
    pricingRoutes,
    galleryRoutes,
    supportRoutes,
    adminRoutes,
  ]
    .flatMap((x) => Object.entries(x))
    .map((kvp): IRouteItem => kvp[1]);

  return fromOldMenus.concat(fromRoutes);
}

export const routes = {
  salesMenu,
  contactsMenu,
  stockMenu,
  distributionMenu,
  productionMenu,
  batchMenu,
  containerTrackingMenu,
  dutyMenuAustralian,
  dutyMenuGerman,
  dutyMenuUk,
  integrationsMenu,
  multiTenantMenu,
  distributionAppMenu,
  unauthorisedMenu,
};

export const showMenuItemForCurrentUser = (menuItem: MenuItem): boolean => {
  const menuItemIsVisible =
    menuItem.visible instanceof Function
      ? menuItem.visible()
      : menuItem.visible;

  if (!menuItemIsVisible) {
    return false;
  }

  // hide if hidden behind a flag the user does not have access to.
  if (
    menuItem.requiredFeatureFlag &&
    !hasFeatureUsingUserStore(menuItem.requiredFeatureFlag)
  ) {
    return false;
  }

  // if we then don't have permission
  if (
    menuItem.requiredUserPermissions &&
    !hasPermissionsUsingUserStore(menuItem.requiredUserPermissions)
  ) {
    return false;
  }

  // If we don't have a view component
  if (!menuItem.view) {
    // check if any submenu item is visible, or if we have none, hide
    return menuItem.subMenus
      ? menuItem.subMenus.some((subMenuItem) =>
          showMenuItemForCurrentUser(subMenuItem)
        )
      : false;
  }

  return true;
};

export const flattenMenu = (
  path: readonly MenuItem[],
  items: readonly MenuItem[]
): readonly PathedMenuItem[] => {
  return items.flatMap((menuItem) =>
    [{ path, menuItem }].concat(
      flattenMenu([...path, menuItem], menuItem.subMenus ?? [])
    )
  );
};

export const menuBreadcrumbText = (
  menuItems: readonly MenuItem[],
  seperator: string = " -> "
) => {
  return menuItems.map((x) => x.displayText()).join(seperator);
};

export const menuSearch = (query: string): IResolvedSearchResult[] => {
  const queryLower = query.toLocaleLowerCase();
  const flattened = flattenMenu([], buildMenuItems());

  return flattened
    .filter(
      (flat) =>
        flat.menuItem.view &&
        flat.menuItem.url &&
        flat.menuItem.displayText().toLocaleLowerCase().indexOf(queryLower) >=
          0 &&
        showMenuItemForCurrentUser(flat.menuItem) &&
        flat.path.every((x) => showMenuItemForCurrentUser(x))
    )
    .map(
      (flat): IResolvedSearchResult => ({
        description: menuBreadcrumbText([...flat.path, flat.menuItem]),
        isHidden: false,
        typeName: "Menu",
        navigationRoute: flat.menuItem.url!,
      })
    );
};
